import { type ReactNode, useState } from 'react'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { BarListRow } from './bar-list-row'

export interface AccordionBarChartItem<TChild> {
	id: string
	label: string
	value: number
	tooltip?: ReactNode
	children?: TChild[]
}

type ChildOf<TParent> =
	TParent extends AccordionBarChartItem<infer TChild> ? TChild : never

interface AccordionBarChartProps<
	TParent extends AccordionBarChartItem<unknown>,
> {
	items: TParent[]
	getChildId: (child: ChildOf<TParent>) => string
	getChildLabel: (child: ChildOf<TParent>) => string
	getChildValue: (child: ChildOf<TParent>) => number
	getChildTooltip?: (child: ChildOf<TParent>) => ReactNode
	colorClass?: string
	childColorClass?: string
	emptyState?: ReactNode
	/** Override the parent row's bar rendering, e.g. for a stacked/multi-color bar. */
	renderRow?: (item: TParent, maxParent: number) => ReactNode
	/** Render children on demand (e.g. lazy-fetched drilldown) instead of item.children. */
	renderChildren?: (item: TParent) => ReactNode
	/** Controlled set of expanded item ids. Falls back to internal state when omitted. */
	expanded?: string[]
	onExpandedChange?: (expanded: string[]) => void
}

export function AccordionBarChart<
	TParent extends AccordionBarChartItem<unknown>,
>({
	items,
	getChildId,
	getChildLabel,
	getChildValue,
	getChildTooltip,
	colorClass = 'bg-kaefer-sapphire-blue',
	childColorClass = 'bg-kaefer-sapphire-blue/50',
	emptyState,
	renderRow,
	renderChildren,
	expanded,
	onExpandedChange,
}: AccordionBarChartProps<TParent>) {
	const [internalExpanded, setInternalExpanded] = useState<string[]>([])
	const expandedValue = expanded ?? internalExpanded
	const setExpandedValue = onExpandedChange ?? setInternalExpanded

	if (items.length === 0) return <>{emptyState}</>

	const maxParent = Math.max(1, ...items.map((item) => item.value))

	return (
		<Accordion
			type="multiple"
			className="mt-2"
			value={expandedValue}
			onValueChange={setExpandedValue}
		>
			{items.map((item) => {
				const children = (item.children ?? []) as ChildOf<TParent>[]
				const maxChild = Math.max(1, ...children.map(getChildValue))

				const row = renderRow ? (
					renderRow(item, maxParent)
				) : (
					<BarListRow
						label={item.label}
						displayValue={String(item.value)}
						widthPct={(item.value / maxParent) * 100}
						colorClass={colorClass}
					/>
				)

				return (
					<AccordionItem key={item.id} value={item.id}>
						<AccordionTrigger className="cursor-pointer py-2.5 hover:no-underline">
							<div className="w-full pr-1">
								{item.tooltip ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<div>{row}</div>
										</TooltipTrigger>
										<TooltipContent side="top" align="center">
											{item.tooltip}
										</TooltipContent>
									</Tooltip>
								) : (
									row
								)}
							</div>
						</AccordionTrigger>
						<AccordionContent className="pl-3">
							{renderChildren ? (
								renderChildren(item)
							) : children.length > 0 ? (
								<div className="flex flex-col gap-2">
									{children.map((child) => {
										const childId = getChildId(child)
										const childTooltip = getChildTooltip?.(child)
										const childRow = (
											<BarListRow
												label={getChildLabel(child)}
												displayValue={String(getChildValue(child))}
												widthPct={(getChildValue(child) / maxChild) * 100}
												colorClass={childColorClass}
												muted
											/>
										)

										return (
											<div key={childId}>
												{childTooltip ? (
													<Tooltip>
														<TooltipTrigger asChild>
															<div>{childRow}</div>
														</TooltipTrigger>
														<TooltipContent side="top" align="center">
															{childTooltip}
														</TooltipContent>
													</Tooltip>
												) : (
													childRow
												)}
											</div>
										)
									})}
								</div>
							) : null}
						</AccordionContent>
					</AccordionItem>
				)
			})}
		</Accordion>
	)
}
